import { useCallback, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { api, errorMessage } from "../lib/api";

declare global {
  interface Window { GoCardlessDropin?: any }
}

const SCRIPT = "https://pay.gocardless.com/billing/static/dropin/v2/initialise.js";

function loadDropin(): Promise<any> {
  if (window.GoCardlessDropin) return Promise.resolve(window.GoCardlessDropin);
  return new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = SCRIPT;
    s.onload = () => resolve(window.GoCardlessDropin);
    s.onerror = () => reject(new Error("Could not load GoCardless"));
    document.head.appendChild(s);
  });
}

export type MandateStage = "idle" | "opening" | "in_modal" | "confirming" | "active" | "error";

/** Opens the GoCardless Drop-in modal in-app; falls back to the hosted redirect (used in mock mode). */
export function useGoCardlessMandate(onActive?: () => void) {
  const [stage, setStage] = useState<MandateStage>("idle");
  const [error, setError] = useState<string>("");
  const qc = useQueryClient();
  const timer = useRef<any>(null);

  const poll = useCallback(async () => {
    setStage("confirming");
    for (let i = 0; i < 20; i++) {
      const { data } = await api.get("/mandates/current");
      if (data.status === "active") {
        qc.invalidateQueries({ queryKey: ["mandate"] });
        qc.invalidateQueries({ queryKey: ["wallet"] });
        setStage("active");
        onActive?.();
        return;
      }
      await new Promise((r) => (timer.current = setTimeout(r, 2000)));
    }
    setStage("error");
    setError("GoCardless has not confirmed the mandate yet. It will appear in your cellar as soon as it is confirmed.");
  }, [qc, onActive]);

  const start = useCallback(async () => {
    setError("");
    setStage("opening");
    try {
      const { data } = await api.post("/mandates/start");
      if (data.mock) {
        window.location.href = data.authorisation_url;
        return;
      }
      const Dropin = await loadDropin();
      const handler = Dropin.create({
        billingRequestFlowID: data.flow_id,
        environment: data.environment,
        onSuccess: () => poll(),
        onExit: (err: any) => {
          setStage(err ? "error" : "idle");
          if (err) setError(err.message || "The bank connection was interrupted.");
        },
      });
      setStage("in_modal");
      handler.open();
    } catch (e) {
      setStage("error");
      setError(errorMessage(e));
    }
  }, [poll]);

  return { start, stage, error, busy: stage === "opening" || stage === "in_modal" || stage === "confirming" };
}
